import { useState } from 'react';
import type { ReactElement } from 'react';
import { formatDate } from '@xdocs/i18n';
import type { ContactSubmission, SubmissionType } from '@xdocs/schemas';
import { Badge, DataTable, Tabs, cn, useI18n } from '@xdocs/ui';
import type { BadgeTone, ColumnDef } from '@xdocs/ui';
import { RoleGate } from '../../../features/admin/index.js';
import { optionalProp } from '../../../lib/optionalProp.js';
import { ContactDetailPanel } from './ContactDetailPanel.js';
import { useContactInboxList } from './useContactInboxList.js';
import { useInboxT } from './useInboxT.js';
import styles from './InboxPage.module.css';

const STATUS_TONE: Record<ContactSubmission['status'], BadgeTone> = {
  new: 'neutral',
  in_progress: 'warning',
  handled: 'success',
};

type InboxTab = 'all' | SubmissionType;

/**
 * `/admin/inbox` (F53) — contact + DMCA triage on the shared `<DataTable>`
 * (E4 §8.3), tabbed by submission type, live-updated over
 * `admin:contact-inbox` (§5.5).
 *
 * Wrapped in `<RoleGate minimum="admin">` (`features/admin/`, sibling M4
 * task) — F53 is `admin`-only; see `PromosPage.tsx`'s doc comment for the
 * defense-in-depth rationale (UX-only, server enforces on every op).
 */
export function InboxPage(): ReactElement {
  const { locale } = useI18n();
  const { t } = useInboxT();
  const [tab, setTab] = useState<InboxTab>('all');
  const {
    rows,
    total,
    nextCursor,
    query,
    onQueryChange,
    onLoadMore,
    isLoading,
    isLoadingMore,
    isError,
    loadMoreError,
    onRetry,
    invalidate,
    justReceivedLive,
  } = useContactInboxList(tab === 'all' ? {} : { submissionType: tab });
  const [openRow, setOpenRow] = useState<ContactSubmission | null>(null);

  const columns: ColumnDef<ContactSubmission>[] = [
    {
      id: 'name',
      header: t('adminInbox:list.column.submitter'),
      primary: true,
      cell: (row) => (
        <div className={cn(styles['submitterCell'])}>
          <span className={cn(styles['submitterName'])}>{row.name}</span>
          <span className={cn(styles['submitterEmail'])}>{row.email}</span>
        </div>
      ),
    },
    {
      id: 'submissionType',
      header: t('adminInbox:list.column.type'),
      cell: (row) => <Badge tone={row.submissionType === 'dmca' ? 'error' : 'info'}>{t(`adminInbox:type.${row.submissionType}`)}</Badge>,
    },
    {
      id: 'subject',
      header: t('adminInbox:list.column.subject'),
      hideOnMobile: true,
      cell: (row) => <span>{row.subject ?? '—'}</span>,
    },
    {
      id: 'status',
      header: t('adminInbox:list.column.status'),
      cell: (row) => <Badge tone={STATUS_TONE[row.status]}>{t(`adminInbox:status.${row.status}`)}</Badge>,
    },
    {
      id: 'createdAt',
      header: t('adminInbox:list.column.received'),
      sortable: true,
      cell: (row) => <span className={cn(styles['metaCell'])}>{formatDate(locale, row.createdAt, { dateStyle: 'medium', timeStyle: 'short' })}</span>,
    },
  ];

  return (
    <RoleGate minimum="admin">
    <div className={cn(styles['page'])}>
      <div className={cn(styles['header'])}>
        <h1 className={cn(styles['title'])}>{t('adminInbox:page.title')}</h1>
        <p className={cn(styles['description'])}>{t('adminInbox:page.description')}</p>
      </div>

      {justReceivedLive && (
        <p role="status" aria-live="polite" className={cn(styles['liveBanner'])}>
          {t('adminInbox:live.newSubmission')}
        </p>
      )}

      <Tabs<InboxTab>
        label={t('adminInbox:page.title')}
        value={tab}
        onChange={setTab}
        items={[
          { value: 'all', label: t('adminInbox:tabs.all') },
          { value: 'contact', label: t('adminInbox:tabs.contact') },
          { value: 'dmca', label: t('adminInbox:tabs.dmca') },
        ]}
      />

      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(row) => row.id}
        query={query}
        onQueryChange={onQueryChange}
        onRowClick={(row) => setOpenRow(row)}
        label={t('adminInbox:page.title')}
        isLoading={isLoading}
        isError={isError}
        onRetry={onRetry}
        total={total}
        nextCursor={nextCursor}
        onLoadMore={onLoadMore}
        isLoadingMore={isLoadingMore}
        {...optionalProp('loadMoreError', loadMoreError)}
        emptyTitle={t('adminInbox:list.empty.title')}
        emptyDescription={t('adminInbox:list.empty.description')}
        noResultsTitle={t('adminInbox:list.noResults.title', { query: query.q ?? '' })}
        onClearFilters={() => {
          const { q: _q, ...rest } = query;
          onQueryChange({ ...rest, page: 1 });
        }}
        searchLabel={t('adminInbox:list.searchLabel')}
        searchPlaceholder={t('adminInbox:list.searchPlaceholder')}
      />

      {openRow && (
        <ContactDetailPanel
          submission={openRow}
          onClose={() => setOpenRow(null)}
          onHandled={(updated) => {
            setOpenRow(updated);
            invalidate();
          }}
        />
      )}
    </div>
    </RoleGate>
  );
}
